import { z } from "zod";
import { USE_KEYS, UseKey } from "./features.js";
import { Industry } from "./industry.js";
import { BILLING_MSG, BillingStatus } from "./billing.js";
import { PASSWORD_MSG, passwordProblem, passwordStrength } from "./password.js";
import { Module, PRODUCT_MSG, Product } from "./product.js";
import { IP_GUARD_MSG } from "./ipGuard.js";
import { OAUTH_ERROR, OAUTH_NONE, OAuthAvailability, OAuthProvider } from "./oauth.js";
import { PASSKEY_ERROR } from "./passkey.js";
import { AccountPreferences } from "./preferences.js";
import { CompanyOrder, SWITCHER_MSG } from "./switcher.js";
import { Labels } from "./vocabulary.js";
import { ACCOUNT_NAME_MSG, AccountName, namePart, optionalNamePart } from "./name.js";
import { AUTO_LOGOUT_MSG, REAUTH_MSG } from "./autoLogout.js";

// ---------------------------------------------------------------------------
// 入力の決めごと（docs/10 §9.2、015）
//   - 会員登録で聞くのは 姓・名（#152、docs/61 ★2）・メールアドレス・パスワード・業種
//   - パスワードは 8文字以上。上限 200（それ以上は Argon2 の負荷）。文字種は強制しない。
//     簡単すぎるもの（よくあるパスワード・数字だけ・同じ文字や abc・123 の並びばかり）は弾く（password.ts、2026-09-22）
//   - 間違いは色でなく文で。文はここに1か所だけ書く（画面と API が同じ文を出す）
// ---------------------------------------------------------------------------

export const MSG = {
  emailInvalid: "メールアドレスの形になっていません（@ が要ります）",
  passwordTooShort: "パスワードは8文字以上にしてください",
  passwordTooLong: "パスワードは200文字までです",
  loginFailed: "メールアドレスかパスワードが違います",
  /** 10回続けて失敗した（30分ロック、#111）。登録の無いメールアドレスにも同じ回数で同じ文（登録の有無を探られない） */
  loginLocked: "ログインに続けて失敗したため、30分ほどログインできません。パスワードを再設定すると解除されます",
  emailTaken: "このメールアドレスは登録済みです。ログインしてください",
  notVerified: "メールの中の「登録を完了する」を押すと始められます",
  tokenInvalid: "このリンクは使えません。もう一度やり直してください",
  tokenExpired: "このリンクは期限が切れています。もう一度やり直してください",
  /**
   * 会社を選ぶ鍵（ログインの続き、10分・1回きり）が使えない（#203）：時間が経った・使った・同じ入口でもう一度ログインした。
   * API の 401 AUTH_CHOOSE_EXPIRED（会社を選ぶ・会社の作成・ほかの製品へ移る）の文。画面は止めの画面 j)（STOP_MSG.loginExpired）。
   * 2026-10-17 夜 射場さん：場面に合った文に（前は「時間が経ちました。もう一度ログインしてください」。DB の HINT は前の文のまま＝画面には出ない detail）
   */
  chooseExpired: "ログインの有効期限が切れました。再度ログインしてください",
  userNotFound: "このメールアドレスの登録がありません。会員登録しますか",
  alreadyVerified: "すでに確認が済んでいます。ログインしてください",
  tenantNotActive: "この会社は現在ご利用いただけません",
  /** 退会の手続き中（026）。ログイン・会員登録で */
  accountClosed: "このメールアドレスは退会の手続き中です。元に戻すには、退会のときに届いたメールのリンクを押してください",
  /** メールサーバーにつながらない・断られた（503。登録はやり直せる） */
  mailFailed: "メールを送れませんでした。少し待ってからもう一度お試しください",
  /** 同じところからの操作が短い時間に続いた（429。ログイン・会員登録・メールの送り直しなど。034） */
  tooMany: "操作が続いたため、いまは受け付けられません。少し時間をおいてからもう一度お試しください",
  /** 混み合っていて DB の接続を待ちきれなかった・SQL が時間の上限を超えた（503 DB_BUSY。性能の改善 F、2026-10-13）。自動保存は 5 秒後に自動で送り直す */
  dbBusy: "混み合っているため処理できませんでした。少し待ってからもう一度お試しください",
  /** PDF の順番待ちが上限を超えた（503 PDF_BUSY。同時に作る数の上限、docs/72 §10、2026-10-13） */
  pdfBusy: "PDF の作成が混み合っているため作成できませんでした。少し待ってからもう一度お試しください",
  /** PDF を作っているあいだ（紙の画面・新しいタブ・ボタン。順番待ちのあいだも） */
  pdfMaking: "PDF 作成中…",
  /** モジュールの門（049、requireModule）。404 MODULE_OFF */
  moduleOff: PRODUCT_MSG.moduleOff,
  /** 自動ログアウト（#189、docs/76）：無操作の上限を超えた（401 SESSION_IDLE。画面は止めの画面 g） */
  sessionIdle: AUTO_LOGOUT_MSG.api,
  /** 本人確認が要る操作（#189）。403 REAUTH_REQUIRED（画面は本人確認の窓を開いて同じ操作を送り直す） */
  reauthRequired: REAUTH_MSG.api,
  /** 違う製品の会社を選んだ・切り替えた（049）。403 PRODUCT_MISMATCH。文にはその会社の製品名とドメインが入る */
  productMismatch: PRODUCT_MSG.productMismatch,
  /** 画面の下の帯（2026-09-18 デザイン見直し）。ログイン前は淡い帯、ログイン後は細いフッター。同じ文を使う */
  footerTerms: "利用規約",
  footerPrivacy: "プライバシーポリシー",
  footerTokushoho: "特定商取引法に基づく表記",
  footerContact: "お問い合わせ",
  footerOperator: "運営：株式会社アイバックス",
  footerCopyright: "© 2026 iBAX Inc.",
  /**
   * パスワード再設定（#115、docs/44 §3 → #287、2026-11-01 射場さん ★4：題は「パスワード再設定」・「？」をやめて本文に1行・
   * ログインの画面のリンクは「パスワードをお忘れの方」。docs/100）。PC版・スマホ版で同じ
   */
  forgotTitle: "パスワード再設定",
  /** /forgot へのリンク（ログイン・会員登録の登録済み・招待のログイン・個人設定のパスワード変更） */
  forgotLink: "パスワードをお忘れの方",
  forgotLead: "登録済みのメールアドレスに、再設定用のリンクを送信します。",
  forgotSubmit: "再設定メール送信",
  forgotSentTitle: "再設定メールを送信しました",
  /** 「{メール}」（太字）のあとに続ける */
  forgotSentBody: " 宛てに再設定用のリンクを送信しました。",
  forgotSentNote: "リンクの有効期限は1時間です。届かない場合は迷惑メールフォルダを確認してください。",
  /** 新しいパスワードの画面（/reset-password）：題・欄・ボタン */
  resetTitle: "パスワード再設定",
  resetField: "新しいパスワード",
  resetSubmit: "再設定",
  /** リンクが使えないときの、/forgot へのリンク */
  resetAgain: "もう一度「パスワード再設定」から",
  backToLogin: "ログイン画面へ",
  /** 会員登録の同意（#120）：「利用規約」「プライバシーポリシー」（footerTerms・footerPrivacy をリンクに）を「・」でつなぎ、このあとに続ける */
  signupConsentTail: "に同意して登録します。",
  /** 会員登録の下（#120）：このあとに文字のボタン「ログイン」 */
  signupHaveAccount: "すでにお持ちの方は",
  /** 確認メールを送りました（会員登録） */
  signupSentNote: "届かないときは迷惑メールのフォルダを確認してください。",
} as const;

export const Email = z
  .string()
  .trim()
  .max(254, MSG.emailInvalid)
  .regex(/^[^@\s]+@[^@\s]+\.[^@\s]+$/, MSG.emailInvalid);

export const Password = z
  .string()
  .min(8, MSG.passwordTooShort)
  .max(200, MSG.passwordTooLong)
  // 8〜200文字でも簡単すぎるものは弾く（画面のバーと同じ物差し。メールアドレスとの重なりは API・画面が別に見る）
  .superRefine((pw, ctx) => {
    if (pw.length < 8 || pw.length > 200) return;
    const s = passwordStrength(pw);
    if (!s.ok) ctx.addIssue({ code: "custom", message: s.reason ?? PASSWORD_MSG.simple });
  });

/** 64桁の hex（015 fn_new_token） */
export const Token = z.string().regex(/^[0-9a-f]{64}$/, MSG.tokenInvalid);

export const SignupRequest = z
  .object({
    /** 氏名（#152、docs/61 ★2・★a）。姓・名とも要る。人（accounts）と所属の両方に入る */
    familyName: namePart(ACCOUNT_NAME_MSG.familyRequired),
    givenName: namePart(ACCOUNT_NAME_MSG.givenRequired),
    email: Email,
    password: Password,
    /** 業種（#142 段4）。画面は製品の業種から1つ選ぶ。省けば製品の既定（現場力＝工務店、営業力＝その他） */
    industry: Industry.optional(),
  })
  // 自分のメールアドレスの一部が入ったパスワードは弾く（password.ts。再設定・招待・変更は API が同じ関数で見る）
  .superRefine((v, ctx) => {
    const p = passwordProblem(v.password, v.email);
    if (p) ctx.addIssue({ code: "custom", path: ["password"], message: p });
  });
export type SignupRequest = z.infer<typeof SignupRequest>;

/** 登録直後の画面「確認メールを送りました」に要るのは送り先だけ */
export const SignupResponse = z.object({
  email: z.string(),
  /** 確認前にもう一度登録した（確認メールを送り直した） */
  resent: z.boolean(),
  /**
   * E0-c（営業力 docs/02 決めごと17）：このメールはもう確認済みのアカウントがあり、この製品の会社はまだ無い（200）。
   * 画面は同じ画面の中でパスワードを聞き、POST /auth/companies で「この製品の会社」を作る。この製品の会社がもうあれば今までどおり 409
   */
  exists: z.boolean().optional(),
});
export type SignupResponse = z.infer<typeof SignupResponse>;

// ---------------------------------------------------------------------------
// E0-c：同じアカウント（メール・パスワード）で、別の製品の会社を作る（営業力 docs/02 決めごと 6・17、docs/infra/E1_実装依頼.md §0）。
//   会社のデータ（顧客・見積・請求書）は写らない。無料トライアルは新しい会社の登録日から
// ---------------------------------------------------------------------------
export const ADD_COMPANY_MSG = {
  /** 会員登録で exists:true（{product} は製品名） */
  signupExists: "既に別のサービスで利用されています。「{product}」で同じアカウントを利用するにはパスワードを入力してください。",
  signupExistsHint: "他のサービスで利用しているアカウントをそのまま利用できます。",
  /** ログインで noCompany:true */
  noCompany: "「{product}」の会社がまだありません。",
  /** ボタン */
  create: "{product}の会社を作成",
  /** ほかの製品の案内（その製品名とドメイン） */
  otherProduct: (name: string, host: string) => `${name}をお使いの方は ${host} へ`,
  /** POST /auth/companies：この製品の会社がもうある（409） */
  exists: "このメールアドレスにはもう{product}の会社があります。ログインしてください",
  /** POST /auth/companies・/account/companies：違う製品の会社は、そのドメインで作る（403） */
  wrongDomain: "{product}の会社は {productHost} で作れます",
} as const;

// ---------------------------------------------------------------------------
// #99 新しい会社を作る（docs/38。#77＝docs/29 の2回目の残り）。#151（docs/60）から：アカウントのメニュー「会社を追加」→ 会社の切替の
//   「会社を追加」の行 → シート（題「新しい会社」・取消／作成）。
//   聞くのは会社名（屋号）だけ。作った人が管理者。データは写らない。料金は会社ごと（30日の無料トライアルから）。
//   自分で作った会社は10社まで（招待で加わった会社は数えない。守りは DB の fn_account_add_company）
// ---------------------------------------------------------------------------
/** 自分で作った会社（会員登録の会社も含む）の上限。DB の fn_account_add_company（20260930_0020）と同じ数 */
export const OWN_COMPANY_LIMIT = 10;
export const NEW_COMPANY_MSG = {
  /**
   * アカウントのメニューと、会社の切替の行（#151 ★h → #185：「会社を追加」から改名。招待を受けるつもりで押して別の会社を作った人がいた）。
   * PC だけ（スマホはメニューから消す＝pcOnly.ts）。#186 で「新しい会社を作る」→「新しい会社の作成」（業務の言葉、docs/10 §5.1.2）
   */
  menu: "新しい会社の作成",
  /** 窓の題（#185。前はシートの「新しい会社」） */
  sheetTitle: "新しい会社の作成",
  /** 題の下の1文（#185 → #186 → #287） */
  lead: "別の会社（事業所）を新しく作成します。作成者が管理者になります。",
  /**
   * 注意の帯（#287、2026-11-01 射場さん ★6：朱の帯はエラーに見える＝藍の淡い地・藍の字・左に ⓘ。文は事実だけ）。current＝現在の会社の名前
   */
  warn: (current: string) => `${current} に利用者を追加する操作ではありません。利用者の追加は 設定 → ユーザー管理 →「ユーザー追加」から行います。`,
  /** 現在の会社が無い（ログインの続き）・名前がまだ無いとき */
  warnNoCurrent: "既存の会社に利用者を追加する操作ではありません。利用者の追加は、その会社の管理者が 設定 → ユーザー管理 から行います。",
  nameLabel: "会社名（屋号）",
  note: "無料トライアル（30日間）から開始します。料金は会社ごとに発生します。",
  /** 窓の右下（会社名が入るまで押せない） */
  submit: "作成",
  cancel: "取消",
  nameRequired: "会社名を入力してください",
  nameTooLong: "会社名は100文字までです",
  limit: `自分で作成した会社は${OWN_COMPANY_LIMIT}社までです`,
  /** 作ったあとのホームのトースト */
  created: (name: string) => `「${name}」を作成しました`,
  /** トーストのボタン（→ 設定 → 自社情報） */
  toCompany: "自社情報入力",
} as const;

/** 新しい会社の名前（前後の空白は落とす。必須・100文字まで。自社情報の会社名と同じ長さ） */
export const NewCompanyName = z.string().trim().min(1, NEW_COMPANY_MSG.nameRequired).max(100, NEW_COMPANY_MSG.nameTooLong);

/** ログインで「この製品の会社が無い」（E0-c。E0-a の 403 PRODUCT_MISMATCH の代わりに 200） */
export const LoginNoCompany = z.object({
  noCompany: z.literal(true),
  /** いまのドメインの製品（作れる会社の製品） */
  product: Product,
  email: z.string(),
  /** 持っている会社の製品（案内「現場力をお使いの方は genbaryoku.jp へ」） */
  otherProducts: z.array(Product),
});
export type LoginNoCompany = z.infer<typeof LoginNoCompany>;

/** POST /auth/companies（ログイン前）：パスワードで本人を確かめて、いまのドメインの製品の会社を作り、そのままログイン */
export const AddCompanyRequest = z.object({
  email: Email,
  password: z.string().min(1, MSG.loginFailed).max(200, MSG.loginFailed),
  /** 省けばいまのドメインの製品。違う製品を送ると 403（Cookie はドメインごと） */
  product: Product.optional(),
  remember: z.boolean().default(true),
  /** 業種（#142 段4）。省けば製品の既定 */
  industry: Industry.optional(),
  /** 氏名（#152）。人に氏名が無ければ要る（無ければ 400 ACCOUNT_NAME_REQUIRED）。あれば見ない */
  familyName: optionalNamePart,
  givenName: optionalNamePart,
});
export type AddCompanyRequest = z.infer<typeof AddCompanyRequest>;

/** POST /account/companies（ログイン中）：同じ人に会社をもう1つ作って切り替える（#99：会社を選ぶ画面の「＋ 新しい会社を作る」。会社名は必須） */
export const AccountAddCompanyRequest = z.object({
  name: NewCompanyName,
  product: Product.optional(),
  industry: Industry.optional(),
  /** 氏名（#152、docs/61 §2 ⑦「あなたの氏名」）。人に氏名が無ければ要る（無ければ 400 ACCOUNT_NAME_REQUIRED）。あれば見ない */
  familyName: optionalNamePart,
  givenName: optionalNamePart,
});
export type AccountAddCompanyRequest = z.infer<typeof AccountAddCompanyRequest>;

/** POST /auth/choose/company（ログインの続き。#99）：会社を選ぶ画面の chooseToken で本人を確かめ、いまのドメインの製品の会社を作ってその会社でログイン */
export const ChooseAddCompanyRequest = z.object({
  chooseToken: Token,
  name: NewCompanyName,
  remember: z.boolean().default(true),
  /** 業種（#142 段4）。省けば製品の既定 */
  industry: Industry.optional(),
  /** 氏名（#152）。人に氏名が無ければ要る（無ければ 400 ACCOUNT_NAME_REQUIRED） */
  familyName: optionalNamePart,
  givenName: optionalNamePart,
});
export type ChooseAddCompanyRequest = z.infer<typeof ChooseAddCompanyRequest>;

export const ResendVerificationRequest = z.object({ email: Email });
export type ResendVerificationRequest = z.infer<typeof ResendVerificationRequest>;

export const VerifyEmailRequest = z.object({ token: Token });
export type VerifyEmailRequest = z.infer<typeof VerifyEmailRequest>;

export const LoginRequest = z.object({
  email: Email,
  password: z.string().min(1, MSG.loginFailed).max(200, MSG.loginFailed),
  /** 「この端末ではログインしたままにする」。既定 ON（docs/10 §9.2） */
  remember: z.boolean().default(true),
});
export type LoginRequest = z.infer<typeof LoginRequest>;

export const RequestPasswordResetRequest = z.object({ email: Email });
export type RequestPasswordResetRequest = z.infer<typeof RequestPasswordResetRequest>;

export const ResetPasswordRequest = z.object({
  token: Token,
  password: Password,
});
export type ResetPasswordRequest = z.infer<typeof ResetPasswordRequest>;

/** 再設定のリンクを開いたとき（041）：誰のパスワードか（画面はメールアドレスをユーザー名の欄に置き、スマホやパソコンが「強いパスワード」をユーザー名つきで覚えられるようにする） */
export const ResetLookup = z.object({
  state: z.enum(["ok", "expired", "used", "invalid"]),
  email: z.string(),
  /** false＝Google・Apple だけで作った人（#112）。画面の題は「パスワード設定」 */
  hasPassword: z.boolean().default(true),
  /** つないである外部 */
  providers: z.array(OAuthProvider).default([]),
});
export type ResetLookup = z.infer<typeof ResetLookup>;

export const Role = z.enum(["admin", "manager", "leader", "member"]);
export type Role = z.infer<typeof Role>;

/** ログイン中の自分。ヘッダーの会社名・名前と、初回の発行で会社名を聞くかどうか */
export const Me = z.object({
  user: z.object({
    id: z.string().uuid(),
    email: z.string(),
    /** 画面に出す名前（「姓 名」＝人の氏名の写し。#152 から全部の会社で同じ。氏名が無い人は空文字） */
    name: z.string(),
    familyName: z.string(),
    givenName: z.string(),
    role: Role,
  }),
  /**
   * 人（accounts）の氏名（#152、docs/61 §4）。全部の会社で同じ。needsName＝姓・名がそろっていない
   * （画面はホームの前に「はじめに」（/welcome）・右上は人の形＋メール・会社を追加で「あなたの氏名」を聞く）
   */
  account: AccountName,
  tenant: z.object({
    id: z.string().uuid(),
    /** 会員登録では聞かないので、初回の発行までは空文字 */
    name: z.string(),
    /** 会社名がまだ無い（初回の発行で聞く） */
    needsCompanyName: z.boolean(),
    /** この会社の言葉（027、docs/22）。「顧客」「工事名」など。画面は useLabels() で読む */
    labels: Labels,
    /** 製品（入口。049）。genba＝現場力／eigyo＝営業力。ログイン後の見た目・料金表はこれが正（ホストと違えば画面はその製品のドメインへ移す） */
    product: Product,
    /** いま有効なモジュール（049、tenant_modules）。画面はメニューを出す／出さないに使う。門は API の requireModule */
    modules: z.array(Module),
    /** 使う機能（#142 段3、docs/52 §3）＝ON のもの。画面は apps/web/src/core/uses.ts の uses() で隠す／出すだけ（API では止めない） */
    uses: z.array(UseKey).default([...USE_KEYS]),
    /** 業種（#142 段4）。NULL＝今ある会社・ログインの画面から初めて入った人（入った直後に選ぶ） */
    industry: Industry.nullable().default(null),
  }),
  session: z.object({
    /** この端末のログインの期限（ISO 8601）。remember なら使うたびに延びる */
    expiresAt: z.string(),
    /** 人（accounts）の番号（#189：入力の退避の鍵。会社が変わっても同じ） */
    accountId: z.string().uuid(),
    /** 会社の無操作の上限（分。#189、docs/76） */
    autoLogoutMinutes: z.number().int(),
    /** 人の操作の時刻（ISO 8601。sessions.last_action_at。X-User-Action の要求のときだけ1分に1回まで進む） */
    lastActionAt: z.string(),
    /** 無操作で切れるまでの残り（秒）。画面の帯の期限はこの値（と毎回の答えのヘッダー x-gb-idle-left）から出す */
    idleLeftSec: z.number().int(),
  }),
  /** 料金プランと無料トライアルの段階（034、docs/26）。readOnly なら画面の上に「見るだけ」の帯を出す */
  billing: BillingStatus,
  /** サーバーの設定で使える／使えないが決まるもの（画面はボタンを出す／出さないに使う） */
  features: z.object({
    /** 国税庁 Web-API（法人番号から住所・番号チェック、028）。会社の製品の ID（現場力＝.env の NTA_APP_ID・営業力＝NTA_APP_ID_EIGYO）があれば true */
    corporateLookup: z.boolean(),
    /** 自分の書式（帳票デザインのオプション、045、docs/31）。受付開始前（BILLING_LIVE_FROM が空）は true。#69 で会社ごとに */
    paperDesign: z.boolean().default(true),
    /** 工程管理（有料オプション、#100、docs/39）。現場力の会社で、受付開始前（BILLING_LIVE_FROM が空）か モジュール schedule が ON なら true。営業力は false */
    schedule: z.boolean().default(false),
    /** Google・Apple でログイン（#112、docs/43）。この製品の鍵（.env）があるものだけ true。設定 → ログイン方法の「追加」に使う */
    oauth: OAuthAvailability.default(OAUTH_NONE),
    /**
     * 案件の地図（#137、docs/49 §13）。provider＝google（Maps Embed／JavaScript。key は画面用の鍵）か gsi（地理院タイル＋ABR）。
     * reverse＝「ピンの位置から住所を入れる」を出せるか（google はサーバー用の鍵、gsi は abr-geocoder があるとき）
     */
    map: z.object({ provider: z.enum(["google", "gsi"]), key: z.string().nullable(), reverse: z.boolean() }).default({ provider: "gsi", key: null, reverse: false }),
    /**
     * 横断検索の AI回答（#224、docs/90 §13）。会社の印（tenants.ai_answer_enabled＝運営が管理アプリで有効にする・既定 false）と
     * サーバーの宛先（.env の SEARCH_LLM_URL）の両方があるときだけ true。false の会社は「Enter で AI回答」・ボタン・質問例を出さない（入力中の検索は出す）
     */
    aiAnswer: z.boolean().default(false),
  }),
  /** 人（accounts）ごとの画面の好み（#115、docs/44 §2）。文字の大きさ（null＝人の設定なし＝端末の Cookie か既定） */
  preferences: AccountPreferences,
  /**
   * この人が入っている会社（040、docs/29）。2つ以上ならアカウントのメニューに「会社の切替」（#151）。
   * 並び（#151、docs/60 §3）：いまの製品の会社が先、ほかの製品が後。その中は 使えない会社が下 → 自分で並べた順（manual）か よく使う順（auto）
   */
  companies: z.array(
    z.object({
      tenantId: z.string().uuid(),
      name: z.string(),
      role: z.enum(["admin", "member"]),
      own: z.boolean(),
      usable: z.boolean(),
      blockedReason: z.enum(["disabled", "closed", "suspended"]).nullable(),
      current: z.boolean(),
      /** その会社の製品（049）。会社を選ぶ画面の帯（「現場力」「営業力」） */
      product: Product,
      /** いまのドメイン（製品）の会社か。違う製品の会社は false＋reason（#151 から押すと相手のドメインへ移って入る） */
      selectable: z.boolean(),
      reason: z.enum(["other_product"]).nullable(),
      /** ロゴの署名つき URL か null（#151。users.ts の CompanyChoice と同じ） */
      logoUrl: z.string().nullable().default(null),
      /** 頭文字（companyInitial）。会社名が空なら null */
      initial: z.string().nullable().default(null),
      /** よく使う順の位置（1 から） */
      autoRank: z.number().int().default(0),
    }),
  ),
  /** 会社の並び順（#151）：auto＝よく使う順（既定）／manual＝自分で並べる。人（accounts）ごと */
  companyOrder: CompanyOrder.default("auto"),
});
export type Me = z.infer<typeof Me>;

/** 015 の RAISE EXCEPTION の文字列 → HTTP の状態と画面の文 */
export const AUTH_ERROR = {
  AUTH_EMAIL_INVALID: { status: 400, title: MSG.emailInvalid },
  // 確認コード（#110、docs/42）。DB の関数が RAISE するもの。ほかの状態（違う・期限切れ・5回）は API が文を決める
  AUTH_CODE_INVALID: { status: 400, title: IP_GUARD_MSG.codeInvalid },
  AUTH_CODE_TOO_MANY: { status: 429, title: IP_GUARD_MSG.codeTooMany },
  AUTH_EMAIL_TAKEN: { status: 409, title: MSG.emailTaken },
  AUTH_EMAIL_NOT_VERIFIED: { status: 403, title: MSG.notVerified },
  AUTH_TOKEN_INVALID: { status: 400, title: MSG.tokenInvalid },
  AUTH_TOKEN_EXPIRED: { status: 410, title: MSG.tokenExpired },
  AUTH_USER_NOT_FOUND: { status: 404, title: MSG.userNotFound },
  AUTH_ALREADY_VERIFIED: { status: 409, title: MSG.alreadyVerified },
  AUTH_TENANT_NOT_ACTIVE: { status: 403, title: MSG.tenantNotActive },
  AUTH_ACCOUNT_CLOSED: { status: 403, title: MSG.accountClosed },
  AUTH_ACCOUNT_ALREADY_CLOSED: { status: 409, title: "この会社はもう退会の手続き中です" },
  AUTH_ACCOUNT_NOT_DUE: { status: 409, title: "まだ消す日ではありません" },
  BILLING_PLAN_INVALID: { status: 400, title: BILLING_MSG.planInvalid },
  BILLING_ADMIN_ONLY: { status: 403, title: BILLING_MSG.adminOnly },
  // 040：ユーザー管理と複数の会社（文は users.ts の USER_MSG と同じ＝stop.test.ts が見比べる。users.ts は auth.ts を読むので、ここは文字で持つ）
  AUTH_USER_DISABLED: { status: 403, title: "この会社では停止中です。管理者に確認してください" },
  AUTH_NO_COMPANY: { status: 403, title: "現在ログインできる会社がありません" },
  AUTH_INVITE_INVALID: { status: 400, title: "この案内のリンクは無効です" },
  AUTH_INVITE_EXPIRED: { status: 410, title: "案内の有効期限（7日間）が切れています。追加者に再送を依頼してください" },
  AUTH_INVITE_USED: { status: 409, title: "この案内は使用済みです。ログインしてください" },
  AUTH_INVITE_CANCELLED: { status: 410, title: "この追加は取り消されています" },
  AUTH_INVITE_EMAIL_MISMATCH: { status: 403, title: "案内メールのアドレスでログインしてください" },
  AUTH_INVITE_LOGIN_REQUIRED: { status: 401, title: "このメールアドレスは{product}に登録済みです。ログインすると利用を開始できます" },
  AUTH_PRODUCT_INVALID: { status: 400, title: "製品は 現場力 か 営業力 です" },
  AUTH_INVITE_PASSWORD_REQUIRED: { status: 400, title: "パスワードを設定してください" },
  AUTH_DEVICE_TOKEN_INVALID: { status: 400, title: MSG.tokenInvalid },
  ROLE_ADMIN_ONLY: { status: 403, title: "この操作は管理者のみ行えます" },
  // #99 新しい会社を作る（20260930_0020）
  ACCOUNT_COMPANY_LIMIT: { status: 409, title: NEW_COMPANY_MSG.limit },
  ACCOUNT_COMPANY_NAME_TOO_LONG: { status: 400, title: NEW_COMPANY_MSG.nameTooLong },
  AUTH_CHOOSE_EXPIRED: { status: 401, title: MSG.chooseExpired },
  // #151 製品をまたぐ切替の鍵（1回きり・2分・同じ端末）。使用済み・期限切れ・端末が違う・会社に入れない はどれも同じ文
  AUTH_HANDOFF_INVALID: { status: 400, title: SWITCHER_MSG.handoffFailed },
  PLAN_SEATS_FULL: { status: 409, title: "利用人数の上限に達しています。プランを変更するか、利用していない利用者を除外してください" },
  USER_ALREADY_MEMBER: { status: 409, title: "この利用者はすでにこの会社に登録されています" },
  USER_NOT_FOUND: { status: 404, title: "該当する利用者がいません" },
  USER_NOT_INVITED: { status: 409, title: "この利用者はすでに利用を開始しています" },
  USER_NOT_JOINED: { status: 409, title: "この利用者は設定待ちです。「追加取消」を使ってください" },
  USER_LAST_ADMIN: { status: 409, title: "管理者がこの利用者1人のため変更できません。先に他の利用者を管理者にしてください" },
  USER_SELF: { status: 409, title: "自分には操作できません。他の管理者が操作してください" },
  USER_ROLE_INVALID: { status: 400, title: "権限は 管理者 か 担当者 です" },
  USER_FAMILY_NAME_REQUIRED: { status: 400, title: "姓を入力してください" },
  // #152 アカウントと氏名（20261008_1800。文は name.ts の ACCOUNT_NAME_MSG）
  ACCOUNT_NAME_REQUIRED: { status: 400, title: ACCOUNT_NAME_MSG.required },
  ACCOUNT_NAME_INVALID: { status: 400, title: ACCOUNT_NAME_MSG.invalid },
  USER_NAME_OWNED: { status: 400, title: ACCOUNT_NAME_MSG.owned },
  // #112 Google・Apple でログイン（20261002_0100。文は oauth.ts の OAUTH_ERROR）
  AUTH_IDENTITY_UNVERIFIED: { status: 400, title: OAUTH_ERROR.AUTH_IDENTITY_UNVERIFIED },
  AUTH_IDENTITY_TAKEN: { status: 409, title: OAUTH_ERROR.AUTH_IDENTITY_TAKEN },
  AUTH_IDENTITY_EXISTS: { status: 409, title: OAUTH_ERROR.AUTH_IDENTITY_EXISTS },
  AUTH_IDENTITY_NOT_FOUND: { status: 404, title: OAUTH_ERROR.AUTH_IDENTITY_NOT_FOUND },
  AUTH_LAST_LOGIN_METHOD: { status: 409, title: OAUTH_ERROR.AUTH_LAST_LOGIN_METHOD },
  AUTH_OAUTH_STATE_INVALID: { status: 400, title: OAUTH_ERROR.AUTH_OAUTH_STATE_INVALID },
  AUTH_OAUTH_STATE_EXPIRED: { status: 400, title: OAUTH_ERROR.AUTH_OAUTH_STATE_EXPIRED },
  AUTH_OAUTH_EXPIRED: { status: 401, title: OAUTH_ERROR.AUTH_OAUTH_EXPIRED },
  AUTH_OAUTH_TOKEN_INVALID: { status: 400, title: OAUTH_ERROR.AUTH_OAUTH_TOKEN_INVALID },
  AUTH_OAUTH_PROVIDER_INVALID: { status: 404, title: OAUTH_ERROR.AUTH_OAUTH_PROVIDER_INVALID },
  // #113 パスキー（20261002_2100。文は passkey.ts の PASSKEY_ERROR）。登録されていない・rp_id（製品のドメイン）が違う鍵は 400
  AUTH_PASSKEY_NOT_FOUND: { status: 400, title: PASSKEY_ERROR.AUTH_PASSKEY_NOT_FOUND },
  AUTH_PASSKEY_COUNTER: { status: 400, title: PASSKEY_ERROR.AUTH_PASSKEY_COUNTER },
  AUTH_PASSKEY_CHALLENGE_INVALID: { status: 400, title: PASSKEY_ERROR.AUTH_PASSKEY_CHALLENGE_INVALID },
  AUTH_PASSKEY_CHALLENGE_EXPIRED: { status: 400, title: PASSKEY_ERROR.AUTH_PASSKEY_CHALLENGE_EXPIRED },
  AUTH_PASSKEY_EXISTS: { status: 409, title: PASSKEY_ERROR.AUTH_PASSKEY_EXISTS },
  AUTH_PASSKEY_LIMIT: { status: 409, title: PASSKEY_ERROR.AUTH_PASSKEY_LIMIT },
  AUTH_PASSKEY_LABEL_INVALID: { status: 400, title: PASSKEY_ERROR.AUTH_PASSKEY_LABEL_INVALID },
} as const;
export type AuthErrorCode = keyof typeof AUTH_ERROR;
